import type { Locale } from 'date-fns/locale';
import { enGB } from 'date-fns/locale';

import type { IDateTimeConfig, IDateTimeVariation } from './types';
import { DateFormats, DateTimeVariation, TimeFormats } from './types';
import { LOCALE } from './enums/locale.enum';
import type { TransKey } from './services/translate/translate.model';

export enum LocalStorageItem {
  AccessToken = 'AccessToken',
  User = 'User',
  ReturnUrl = 'ReturnUrl',
  AuthTime = 'AuthTime',
  SidebarState = 'SidebarState',
  SecondarySidebarState = 'SecondarySidebarState',
  CommonFilterAssigneeOrOwner = 'CommonFilterAssigneeOrOwner',
  CommonFilterProject = 'CommonFilterProject',
  IssueSearchSortOption = 'IssueSearchSortOption',
  TicketSearchSortOption = 'TicketSearchSortOption',
  IssueSortOption = 'IssueSortOption',
  TicketSortOption = 'TicketSortOption',
  TaskSortOption = 'TaskSortOption',
  TableSortOption = 'TableSortOption',
  TableUserMention = 'TableUserMention',
  ReportSortOption = 'ReportSortOption',
  ProcessSortOption = 'ProcessSortOption',
  SearchProcessSortOption = 'SearchProcessSortOption',
  ProjectSortOption = 'ProjectSortOption',
  TopicSortOption = 'TopicSortOption',
  TemplateSortOption = 'TemplateSortOption',
  ReportIssueSortOption = 'ReportIssueSortOption',
  WebSearchLanguageFilter = 'WebSearchLanguageFilter',
  WebSearchRangeFilter = 'WebSearchRangeFilter',
  CommonListRangeFilter = 'CommonListRangeFilter',
  WebSearchTypeJSONFilter = 'WebSearchTypeJSONFilter',
  WebSearchSorting = 'WebSearchSorting',
  WebSearchTreeIds = 'WebSearchTreeIds',
  WebSearchTab = 'WebSearchTab',
  WebSearchExpandedTreeNodes = 'WebSearchExpandedTreeNodes',
  ActiveIssueTab = 'ActiveIssueTab',
  ActiveTicketTab = 'ActiveTIcketTab',
  NewIssueTitle = 'NewIssueTitle',
  NewIssueDescription = 'NewIssueDescription',
  ExistingIssueTitle = 'ExistingIssueTitle',
  ExistingIssueDescription = 'ExistingIssueDescription',
  StickyCalendarView = 'StickyCalendarView',
  HideWeekendCalendar = 'HideWeekendCalendar',
  ShowCompletedTasks = 'ShowCompletedTasks',
  InviteUserType = 'InviteUserType',
  InviteLocale = 'InviteLocale',
  ListOfStickyBoards = 'ListOfStickyBoards',
  GuideVideoProgress = 'GuideVideoProgress',
  ExportSettings = 'ExportSettings',
  AreInternalItemSidesSwapped = 'AreInternalItemSidesSwapped',
  UserTableColumnOrderState = 'UserTableColumnOrderState',
  NewTicketTitle = 'NewTicketTitle',
  NewTicketDescription = 'NewTicketDescription',
  ExistingTicketTitle = 'ExistingTicketTitle',
  ExistingTicketDescription = 'ExistingTicketDescription',
  TableAttachWithoutFilling = 'TableAttachWithoutFilling',
  BoardPageByDefault = 'BoardPageByDefault',
  SearchTreeVisibility = 'SearchTreeVisibility',
  AppVersion = 'AppVersion',
  LastVisitedTableViews = 'LastVisitedTableViews',
  InboxExtraFilters = 'InboxExtraFilters',
  CalendarExtraFilters = 'CalendarExtraFilters',
  DashboardTabIndex = 'DashboardTabIndex',
  RecentlyViewed = 'RecentlyViewed',
  MonitoringSortOption = 'MonitoringSortOption',
  TopicInboxSortOption = 'TopicInboxSortOption',
  // Lower-cased because the standalone compare page has always written this key that way.
  CompareChanges = 'compareChanges',
}

export const REPORT_DETAIL_PAGE = 'REPORT_DETAIL_PAGE';

export const PROJECT_DESC_MAX_LENGTH = 128;
export const PROJECT_NAME_MAX_LENGTH = 128;
export const PROJECT_TAG_MAX_LENGTH = 50;
export const TOPIC_MAX_TERMS = 100;

export const ALL_NODES_ARE_UNCHECKED = 'ALL_NODES_ARE_UNCHECKED';

export const FAKE_PLACEHOLDER_ID = 'FAKE_PLACEHOLDER_ID';

export enum ReportsIssues {
  PUBLISHED = 'published',
  UNPUBLISHED = 'unpublished',
}

export enum IssuesTypes {
  OPEN = 'open',
  CLOSED = 'closed',
  BOOKMARKED = 'bookmarked',
  DRAFTED = 'draft',
  CREATED = 'created',
}

export enum TemplatesTypes {
  ISSUES = 'issues',
  TICKETS = 'tickets',
}

export enum TasksTypes {
  PENDING = 'pending',
  COMPLETED = 'completed',
  ALL = 'all',
  BOOKMARKED = 'bookmarked',
}

export enum SearchItemsTypes {
  INBOX = 'inbox',
  ALL = 'all',
  BLOCKED = 'blocked',
}

export enum CommonListsCategories {
  ALL = 'all',
  BOOKMARKED = 'bookmarked',
  OPEN = 'open',
  CLOSED = 'closed',
}

export const MAX_FILE_SIZE_STANDALONE_COMPARE = 2 * 32 * 1024 * 1024;
export const MAX_FILE_SIZE_AVATAR = 8 * 1024 * 1024;
export const MAX_FILE_SIZE_ATTACHMENT = 64 * 1024 * 1024;

export const DEFAULT_USER_LOCALE = LOCALE.ENGLISH;

export const MONITORING_NAME_LENGTH = 80;

export const SNACK_BAR_DURATION = 6000;
export const MIN_DIALOG_WIDTH = window.innerWidth > 599 ? '300px' : '95%';

export const DEFAULT_ISSUE_SORT_OPTION = '-created_at';
export const DEFAULT_ISSUE_SEARCH_SORT_OPTION = 'relevancy';
export const DEFAULT_TASK_SORT_OPTION = '-created_at';
export const DEFAULT_SEARCH_SORT_OPTION = 'relevancy';

export const NEWEST_SORT_OPTION = 'newest';
export const OLDEST_SORT_OPTION = 'oldest';

const FORMAT_SET: [DateFormats, TimeFormats][] = [
  [DateFormats.A, TimeFormats.A],
  [DateFormats.B, TimeFormats.B],
  [DateFormats.C, TimeFormats.B],
  [DateFormats.D, TimeFormats.B],
  [DateFormats.E, TimeFormats.A],
  [DateFormats.F, TimeFormats.A],
  [DateFormats.G, TimeFormats.A],
  [DateFormats.H, TimeFormats.C],
];

const DATE_TIME_CONFIG: IDateTimeConfig[] = FORMAT_SET.map(([date, time]) => ({
  date,
  time,
  dateTime: `${date} ${time}`,
}));

export const LOCAL_DATE_TIME_VARIATION = new DateTimeVariation({
  date: DateFormats.Local,
  time: TimeFormats.Local,
  dateTime: `${DateFormats.Local} ${TimeFormats.Local}`,
  prefix: 'SETTINGS.LOCAL',
});

export const DATE_FORMATS: ReadonlyArray<IDateTimeVariation> = DATE_TIME_CONFIG.map(v => new DateTimeVariation(v));

export const ANY_LANGUAGE = 'any';
export const NO_RANGE = '∞';

export const NO_FILTER = 'all';

export const DEFAULT_SCROLL_OPTIONS = {
  scrollThrottle: 0,
  scrollDistance: 2,
};
export enum OrderByOption {
  RELEVANCY = 'relevancy',
  TITLE = '+title',
  NAME = '+name',
}

export const COMMON_LIST_TABS = {
  [CommonListsCategories.ALL]: { name: 'PROJECT.ALL_TAB' as TransKey, order: 0 },
  [CommonListsCategories.BOOKMARKED]: { name: 'PROJECT.BOOKMARKED' as TransKey, order: 1 },
};

export enum CustomValidationValue {
  MismatchedPasswords = 'mismatchedPasswords',
  NotValidHex = 'notValidHex',
}

export const DEFAULT_LOCALE: Locale = enGB;

export enum LoadingStatus {
  NotLoading = 'NotLoading',
  Loading = 'Loading',
  Loaded = 'Loaded',
}

export const SORT_DIRECTION = {
  asc: '-',
  desc: '+',
};

export const DATE_RANGES = {
  'LARI.DATE_RANGE.ANY': NO_RANGE,
  'LARI.DATE_RANGE.CUSTOM_RANGE': undefined,
  'LARI.DATE_RANGE.LAST_3_DAYS': 'now-3d/d',
  'LARI.DATE_RANGE.LAST_7_DAYS': 'now-7d/d',
  'LARI.DATE_RANGE.LAST_2_WEEKS': 'now-2w/d',
  'LARI.DATE_RANGE.LAST_MONTH': 'now-1M/d',
  'LARI.DATE_RANGE.LAST_3_MONTHS': 'now-3M/d',
  'LARI.DATE_RANGE.LAST_6_MONTHS': 'now-6M/d',
  'LARI.DATE_RANGE.LAST_YEAR': 'now-1y',
};

export const DATE_RANGES_FUTURE = {
  'LARI.DATE_RANGE.NEXT_3_DAYS': 'now+3d/d',
  'LARI.DATE_RANGE.NEXT_7_DAYS': 'now+7d/d',
  'LARI.DATE_RANGE.NEXT_2_WEEKS': 'now+2w/d',
  'LARI.DATE_RANGE.NEXT_MONTH': 'now+1M/d',
  'LARI.DATE_RANGE.NEXT_3_MONTHS': 'now+3M/d',
  'LARI.DATE_RANGE.NEXT_6_MONTHS': 'now+6M/d',
  'LARI.DATE_RANGE.NEXT_YEAR': 'now+1y',
};

export const KEYWORD_SLICE_LIMIT = 10;

export const FALLBACK_TABLE_COLUMN_WIDTH = 200;

export const NAVBAR_HEIGHT = 96;

export enum ExtraUserPermissions {
  TicketingOnly = 'TicketingOnly',
  CanCreateLariItems = 'CanCreateLariItems',
}

export const disallowedJWTRoutes = [
  '/api/api-token-auth/',
  /\/api\/password-reset\/*/,
  '/api/login-features/',
  '/api/api-token-verify/',
  '/api/api-token-refresh/',
];

export enum IssueLogTypes {
  Issue,
  Ticket,
  TicketDraft,
}

export enum DateFilterKeys {
  Equals = 'equals',
  GreaterThan = 'greaterThan',
  LessThan = 'lessThan',
  NotEqual = 'notEqual',
  InRange = 'inRange',
}

export enum RelativeDateFilterKeys {
  Today = 'today',
  NextWeek = 'nextWeek',
  NextMonth = 'nextMonth',
  Next3Months = 'next3Months',
  LastWeek = 'lastWeek',
  LastMonth = 'lastMonth',
  Last3Months = 'last3Months',
}

export type DateFilterType = DateFilterKeys | RelativeDateFilterKeys;

export const relativeDateFilterValues = {
  [RelativeDateFilterKeys.Today]: 0,
  [RelativeDateFilterKeys.NextWeek]: 7,
  [RelativeDateFilterKeys.NextMonth]: 30,
  [RelativeDateFilterKeys.Next3Months]: 90,
  [RelativeDateFilterKeys.LastWeek]: -7,
  [RelativeDateFilterKeys.LastMonth]: -30,
  [RelativeDateFilterKeys.Last3Months]: -90,
};

export const isRelativeDateFilterKey = (type: DateFilterType): type is RelativeDateFilterKeys => type in relativeDateFilterValues;

export const isDateFilterType = (type: unknown): type is DateFilterType =>
  Object.values<unknown>(DateFilterKeys).includes(type) || Object.values<unknown>(RelativeDateFilterKeys).includes(type);

export const DROPDOWN_ITEMS_LIMIT = 7;

export enum DocXExportEntity {
  Table = 'table',
  FilteredTable = 'filteredtable',
  Issue = 'issue',
  Report = 'report',
}

export enum AttachmentParamType {
  Any = 'any',
  None = 'none',
}
